import { requestGetJson, requestJson, uploadFiles } from "../../../packages/fetcher/fetcherFunctions.js";
import { createArrayValidator, validateUuidV4 } from "../../../packages/validator/validator.js";
import { AccountItem, validateAccountItem } from "../../model/accounting/account/AccountItem.js";
import { Month } from "../../model/accounting/date/Month.js";
import { Entry, validateEntry } from "../../model/accounting/entry/Entry.js";
import { EntrySummary, validateEntrySummary } from "../../model/accounting/entry/EntrySummary.js";
import { NewEntry } from "../../model/accounting/entry/NewEntry.js";
import { VatReport, validateVatReport } from "../../model/accounting/vat/VatReport.js";
import { EntityFile, validateEntityFile } from "../../model/files/EntityFile.js";

/**
 * Retrieves VAT report.
 */
export const fetchVatReport = async (year: string, month: Month) => {
  const report = await requestGetJson<VatReport>(`/api/accounting/vat/${year}/${month}`);
  return validateVatReport(report);
};

/**
 * Retrieves entries.
 */
export const getEntries = async (year: string, month: string) => {
  const entries = await requestJson<EntrySummary[], undefined>(
    `/api/accounting/entries/${year}/${month}`,
    "GET",
    undefined
  );
  return createArrayValidator(validateEntrySummary)(entries);
};

/**
 * Retrieves one entry.
 */
export const getEntry = async (entryId: string) => {
  const entry = await requestJson<Entry, undefined>(`/api/accounting/entry/${entryId}`, "GET", undefined);
  return validateEntry(entry);
};

/**
 * Retrieves one entry files.
 */
export const getEntryFiles = async (entryId: string) => {
  const files = await requestJson<EntityFile[], undefined>(
    `/api/accounting/entry/${entryId}/files`,
    "GET",
    undefined
  );
  return createArrayValidator(validateEntityFile)(files);
};

/**
 * Retrieves account items.
 */
export const getAccountItems = async (accountId: string, year: string, month: string) => {
  const accountItems = await requestJson<AccountItem[], undefined>(
    `/api/accounting/account/${accountId}/items/${year}/${month}`,
    "GET",
    undefined
  );
  return createArrayValidator(validateAccountItem)(accountItems);
};

/**
 * Saves new entry.
 */
export const saveEntry = async (entry: NewEntry) => {
  const entryId = await requestJson<string, NewEntry>(`/api/accounting/entry`, "POST", entry);
  return validateUuidV4(entryId);
};

/**
 * Updates an existing entry.
 */
export const updateEntry = async (entryId: string, entry: NewEntry) => {
  await requestJson<void, NewEntry>(`/api/accounting/entry/${entryId}`, "PUT", entry);
};

/**
 * Uploads files to the entry.
 */
export const uploadEntryFiles = async (entryId: string, formData: FormData) => {
  await uploadFiles(`/api/accounting/entry/${entryId}/files`, formData);
};

/**
 * Removes the given entry.
 */
export const removeEntry = async (entryId: string) => {
  await requestJson<void, undefined>(`/api/accounting/entry/${entryId}`, "DELETE", undefined);
};

/**
 * Removes the given entry file.
 */
export const removeEntryFile = async (entryId: string, fileId: string) => {
  await requestJson<void, undefined>(`/api/accounting/entry/${entryId}/file/${fileId}`, "DELETE", undefined);
};
